import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Whether this drawer instance pushed the current history entry.
   * Tracked on the instance (not read back from `history.state`) so that a
   * cart re-render clearing/desyncing the global state can't make the close
   * handler call `history.back()` against a foreign entry, which would
   * navigate to the previous page and reload it on mobile.
   * @type {boolean}
   */
  #ownsHistoryEntry = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!this.#ownsHistoryEntry) {
      history.pushState({ cartDrawerOpen: true }, '');
      this.#ownsHistoryEntry = true;
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    // Only step back when we actually pushed the entry ourselves, so closing
    // never navigates to (and reloads) the previous page.
    if (this.#ownsHistoryEntry) {
      this.#ownsHistoryEntry = false;
      history.back();
    }
  };

  #handlePopState = async () => {
    // The browser has already popped our pushed entry, so we no longer own it.
    // Clearing the flag first stops closeDialog()'s close handler from calling
    // history.back() a second time.
    this.#ownsHistoryEntry = false;

    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // Quick-adds from the favorites drawer shouldn't hijack focus by auto-opening
    // the cart on top of the favorites popup; the cart icon/contents still update.
    const fromFavorites = event.detail?.data?.source === 'favorites';
    const target = event.target instanceof Element ? event.target : null;
    const fromQuickAdd = target?.closest('.quick-add-modal') != null;

    if (fromQuickAdd && !fromFavorites) {
      // Always surface the cart after a quick-add, regardless of the auto-open
      // setting. The quick-add modal closes itself on this same event, so defer
      // opening until it has finished closing — otherwise the two <dialog>
      // modals fight over the modal state and body scroll lock.
      this.#openAfterQuickAdd();
    } else if (this.hasAttribute('auto-open') && !fromFavorites) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Opens the cart drawer once the quick-add modal has finished closing.
   */
  #openAfterQuickAdd() {
    const dialog = document.getElementById('quick-add-dialog')?.querySelector('dialog');

    if (dialog?.open) {
      dialog.addEventListener('close', () => this.showDialog(), { once: true });
    } else {
      this.showDialog();
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
